import type { ReactNode } from 'react';
import { Pressable, ScrollView, Text as RNText, View } from 'react-native';
import { useRouter } from 'expo-router';

import { DocumentTitle } from '@/components/layout/DocumentTitle';
import { Text } from '@/components/ui';
import { cn } from '@/lib/design-system/cn';
import { addMonths, monthLabel } from '@/lib/finance/month';

export type FinanceTab = 'transactions' | 'budgets' | 'reports' | 'master';

const TABS: { key: FinanceTab; label: string; href: string }[] = [
  { key: 'transactions', label: 'Transactions', href: '/finance/transactions' },
  { key: 'budgets', label: 'Budgets', href: '/finance/budgets' },
  { key: 'reports', label: 'Reports', href: '/finance/reports' },
  { key: 'master', label: 'Master Data', href: '/finance/master' },
];

function MonthSelector({ month, onChange }: { month: string; onChange: (month: string) => void }) {
  return (
    <View className="flex-row items-center gap-1">
      <Pressable
        aria-label="Previous month"
        className="rounded px-2 py-1 hover:bg-surface-raised"
        onPress={() => onChange(addMonths(month, -1))}
      >
        <RNText className="text-base text-fg-muted">‹</RNText>
      </Pressable>
      <Text variant="body-sm" className="min-w-[120px] text-center font-medium">
        {monthLabel(month)}
      </Text>
      <Pressable
        aria-label="Next month"
        className="rounded px-2 py-1 hover:bg-surface-raised"
        onPress={() => onChange(addMonths(month, 1))}
      >
        <RNText className="text-base text-fg-muted">›</RNText>
      </Pressable>
    </View>
  );
}

type FinanceShellProps = {
  activeTab: FinanceTab;
  children: ReactNode;
  month?: string;
  onMonthChange?: (month: string) => void;
  /** Operational tabs show the shared month selector; Master Data hides it. */
  showMonth?: boolean;
};

export function FinanceShell({
  activeTab,
  children,
  month,
  onMonthChange,
  showMonth = false,
}: FinanceShellProps) {
  const router = useRouter();

  return (
    <View className="flex-1 bg-surface">
      <DocumentTitle title="Finance" />
      <View className="flex-row flex-wrap items-center gap-2 border-b border-border px-page py-3">
        <Pressable
          aria-label="Record a transaction"
          className="mr-1 h-8 w-8 items-center justify-center rounded-full bg-accent"
          onPress={() => router.push('/finance/transactions/new')}
        >
          <svg width={14} height={14} viewBox="0 0 14 14" aria-hidden="true">
            <path
              d="M7 1.5v11M1.5 7h11"
              className="text-fg-inverse"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
            />
          </svg>
        </Pressable>
        {TABS.map((tab) => {
          const active = tab.key === activeTab;
          return (
            <Pressable
              key={tab.key}
              className={cn('rounded-md px-3 py-1.5', active ? 'bg-surface-raised' : '')}
              onPress={() => router.push(tab.href)}
            >
              <Text
                variant="body-sm"
                className={active ? 'font-semibold text-accent' : 'text-muted'}
              >
                {tab.label}
              </Text>
            </Pressable>
          );
        })}
        <View className="flex-1" />
        {showMonth && month && onMonthChange ? (
          <MonthSelector month={month} onChange={onMonthChange} />
        ) : null}
      </View>
      <ScrollView className="min-h-0 flex-1" contentContainerClassName="px-page py-page-y">
        {children}
      </ScrollView>
    </View>
  );
}
